import axios from 'axios'

import { readTokenBody } from '@/lib/auth/tokenBody'
import { readTokenHeaders } from '@/lib/auth/tokenHeaders'
import type { TokenView } from '@/lib/auth/types'

import {
  API_BASE_URL,
  APP_VERSION,
  AUTH_PATHS,
  OS_TYPE,
  REFRESH_LOCK_KEY,
  REFRESH_TIMEOUT_MS,
} from './config'
import { SessionExpiredError, toApiError } from './errors'
import { attachApiResponseLogger } from './responseLogger'
import { tokenStore } from './tokenStore'

/**
 * 갱신 전용 클라이언트 — **인터셉터가 없다**(설계 불변식 2의 유일한 예외).
 * `client` 를 재사용하면 401 인터셉터가 자기 자신을 다시 부르는 무한 재귀가 된다.
 */
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REFRESH_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
})

attachApiResponseLogger(refreshClient)

/** 같은 탭 안의 동시 요청이 갱신을 N번 돌지 않게 하나로 묶는다. */
let inFlight: Promise<TokenView> | null = null

async function callRefresh(): Promise<TokenView> {
  const refreshToken = tokenStore.refreshToken
  if (!refreshToken) throw new SessionExpiredError()

  let response
  try {
    response = await refreshClient.post(
      AUTH_PATHS.refresh,
      { deviceId: tokenStore.deviceId, refreshToken },
      { headers: { 'Os-Type': OS_TYPE, 'App-Version': APP_VERSION } }
    )
  } catch (error) {
    const apiError = toApiError(error)
    // 🚨 세션 만료는 **refresh 호출 자체의 401** 뿐이다(설계 불변식 5).
    // 네트워크 오류·5xx 는 세션을 지우지 않는다 — 잠시 뒤 다시 되면 그대로 쓸 수 있다.
    if (apiError.status === 401) throw new SessionExpiredError()
    throw apiError
  }

  // ⚠️ 재발급 응답도 로그인과 같은 규약이다 — **바디 우선, 헤더 폴백**(설계 D1).
  const view = readTokenBody(response.data) ?? readTokenHeaders(response.headers)
  if (!view || !tokenStore.saveTokens(view)) throw new SessionExpiredError()
  return view
}

async function runWithLock<T>(fn: () => Promise<T>): Promise<T> {
  // Web Locks 로 **탭 간** 중복 갱신을 막는다. 서버가 refresh 토큰을 회전시키므로
  // 두 탭이 동시에 갱신하면 한쪽이 이미 회전된 토큰을 들고 401 을 맞는다.
  const locks = (navigator as Navigator & { locks?: LockManager }).locks
  if (!locks?.request) return fn()
  return locks.request(REFRESH_LOCK_KEY, fn) as Promise<T>
}

/**
 * access 토큰을 갱신한다. 동시 호출은 하나로 합쳐진다.
 *
 * @throws {SessionExpiredError} refresh 토큰이 없거나, 갱신이 401 이거나, 저장에 실패한 경우
 * @throws {ApiError} 그 외(네트워크·5xx) — 세션은 유지된다
 */
export function refreshTokens(): Promise<TokenView> {
  if (inFlight) return inFlight

  inFlight = runWithLock(callRefresh).finally(() => {
    inFlight = null
  })
  return inFlight
}

/** 테스트 전용 — 모듈 수준 in-flight 상태 초기화. */
export function __resetRefreshStateForTest(): void {
  inFlight = null
}
